/**
 * RPC Error Utilities
 *
 * Shared utilities for extracting and parsing error messages from Hono RPC responses.
 *
 * @module lib/rpc/errorUtils
 */

/**
 * Extract error message from API response
 *
 * Uses text() first then parses as JSON to handle Hono RPC responses correctly.
 * This approach works because:
 * 1. Hono can return plain text or JSON errors
 * 2. Reading as text first avoids "body already read" errors
 * 3. We then try to parse as JSON to get structured error messages
 *
 * @param res - The fetch Response object
 * @param fallback - Fallback message if extraction fails
 * @returns The extracted error message
 */
export async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text();
  try {
    const errorBody = JSON.parse(text);
    return errorBody.error || errorBody.message || fallback;
  } catch {
    return text || fallback;
  }
}

/**
 * Throw an error with the API error message extracted from the response.
 * Reads the response body as JSON and extracts the `error` field.
 *
 * @param res - The non-ok fetch Response object
 * @param fallback - Fallback message if extraction fails
 */
export async function throwApiError(res: Response, fallback: string): Promise<never> {
  const message = await extractErrorMessage(res, fallback);
  throw new Error(message);
}

/**
 * Parse error body from response text
 *
 * Attempts to parse JSON error body, returns structured object with fallback.
 * Useful when you need the full error object, not just the message.
 *
 * @param text - The response text to parse
 * @param fallback - Fallback message if parsing fails
 * @returns Parsed error object or default with fallback message
 */
export function parseErrorBody(text: string, fallback: string): Record<string, unknown> {
  try {
    return JSON.parse(text);
  } catch {
    return { message: fallback };
  }
}
